// Libraries
import React from 'react';
import moment from 'moment';
// Components
import { Icon } from '@components/icon';
import { SubTitle } from '@components/title';
import { Button, ButtonSectionIcon } from '@components/button';
import { BannerPlan } from '@components/banner-plan';
import { InformativeDate } from '../informative-date/InformativeDate';
// Hooks
import { useInitializeData } from '../hooks/useInitializeData';
// Utils
import { capitalizeCase, parseCompanyName } from '@utils/Text';
import { formatCurrencyNoSymbol } from '@utils/Number';
// Styles
import './InsidePlatform.scss';
import { TooltipInformation } from '@components/tooltip';
import { TOOLTIP_INFORMATION } from '@pages/home/constants/InsidePlatform';
import { ModalTermsConditions, Remembrance } from '@pages/home/components';
import { DueDiligenceNotificationBanner } from '@pages/home/components/due-diligence/dueDiligenceNotificationBanner';
import { useEmployee } from '@pages/employees/hooks/useEmployee';
import { CreateEmployee } from '@pages/employees/components';
import { useReinitializeDetails } from '@pages/employees/hooks/useInitialData';
import { Modal } from '@components/modal';
import { useAppSelector } from '@hooks/redux';
import { RootState } from '@redux/rootReducer';
import { DUE_DILIGENCE_ERROR, DUE_DILIGENCE_FORM_REQUIREMENT, DUE_DILIGENCE_VALIDATION_TEXT } from '@constants/DueDiligence';

export const InsidePlatform: React.FC = () => {
    const {
        data,
        openRenewalBanner,
        openContractsTooltip,
        employees,
        activePlan,
        isUsingFreeDemo,
        openDueDiligenceBanner,
        dueDiligenceLink,
        openTermsAndConditionsModal,
        dueDiligenceModal,
        dueDiligenceStatus,
        showTyCErrorMessage,
        handleYearPeriodText,
        handleLiquidation,
        handleOpenDueDiligenceRedirecction,
        handleShowInformativeContractExpiration,
        handleStartTour,
        handleShowTermsAndConditionsModal,
        openDocumentPdf,
        approveNewTermsAndConditionsVersion,
        validateDueDiligenceStatus,
    } = useInitializeData();
    const {
        company: { information },
    } = useAppSelector((state: RootState) => state);
    const { createEmployee, handleModal, selectedEmployee, editEmployee, handleActionEmployeeById, viewEmployee } = useEmployee();

    const { clearDetail } = useReinitializeDetails();

    const dueDiligenceModalConfig = {
        ERROR: {
            modalText: DUE_DILIGENCE_ERROR,
            buttonText: 'Recargar',
            action: validateDueDiligenceStatus,
        },
        UPDATE: {
            modalText: DUE_DILIGENCE_VALIDATION_TEXT,
            buttonText: 'Aceptar',
            action: validateDueDiligenceStatus,
        },
        REDIRECT: {
            modalText: DUE_DILIGENCE_FORM_REQUIREMENT,
            buttonText: 'Confirmar',
            action: handleOpenDueDiligenceRedirecction,
        },
    };

    const dueDiligenceConfig =
        dueDiligenceModal && dueDiligenceModal in dueDiligenceModalConfig
            ? dueDiligenceModalConfig[dueDiligenceModal as keyof typeof dueDiligenceModalConfig]
            : null;

    return (
        <>
            <CreateEmployee
                edit={editEmployee}
                showModal={createEmployee}
                handleModal={handleModal}
                actionModal={(): void => {
                    handleModal();
                    clearDetail();
                }}
                employee={selectedEmployee}
                isEditLiquidator
                defaultStep={1}
                viewEmployee={viewEmployee}
            />

            <div className="inside-platform flex-column">
                {openRenewalBanner && <Remembrance actualPlan={activePlan} isFreeDemoActive={isUsingFreeDemo} />}
                {openDueDiligenceBanner && <DueDiligenceNotificationBanner link={dueDiligenceLink} status={dueDiligenceStatus} />}
                {openTermsAndConditionsModal && !isUsingFreeDemo && (
                    <ModalTermsConditions
                        showErrorMessage={showTyCErrorMessage}
                        handleOpenDocument={openDocumentPdf}
                        handleBackFunction={(): void => handleShowTermsAndConditionsModal(false)}
                        handleSubmitTermsConditions={approveNewTermsAndConditionsVersion}
                    />
                )}
                {activePlan?.is_active && employees?.length <= 0 && <BannerPlan months={activePlan?.total_months} />}
                <Modal isOpen={!!dueDiligenceConfig} closeIcon={false}>
                    <div className="data-company__modal-redirect">
                        <p>{dueDiligenceConfig?.modalText}</p>
                        <div className="data-company__modal-redirect--button-container">
                            <Button onClick={dueDiligenceConfig?.action}>{dueDiligenceConfig?.buttonText}</Button>
                        </div>
                    </div>
                </Modal>
                <div className="inside-platform__header">
                    <div className="container__title flex-row">
                        <h1 className="title">
                            Bienvenido {parseCompanyName(information?.name || '', information.document_type_id!)}
                        </h1>
                    </div>
                    <div className="container__pointer" id="pointer" onClick={handleStartTour}>
                        <div className="container__icon">
                            <Icon name="pointerWhite" className="pointer--icon" />
                        </div>
                        <div className="container__text">
                            <label className="text">Conoce nuestro portal</label>
                        </div>
                    </div>
                </div>
                <div className="inside-platform__body flex-column">
                    <div className="summary-month__container flex-column">
                        <SubTitle title={`Resumen ${capitalizeCase(moment().format('MMMM YYYY'))}`} className="title" />
                        <div className="container__main-card flex-row">
                            <div className="main-card flex-row">
                                <div className="main-card--column flex-column">
                                    <label className="column--title">Total empleados liquidados </label>
                                    <div className="value__container flex-row">
                                        <label className="value">{data?.month_summary?.pay_off_employees || 0}</label>
                                    </div>
                                </div>
                                <div className="main-card--column flex-column">
                                    <label className="column--title">Nuevos ingresos</label>
                                    <div className="value__container flex-row">
                                        <label className="value">{data?.month_summary?.new_incomes || 0}</label>
                                    </div>
                                </div>
                                <div className="main-card--column flex-column">
                                    <label className="column--title">Total contratos terminados</label>
                                    <div className="value__container flex-row">
                                        <label className="value">{data?.month_summary?.finished_contracts || 0}</label>
                                    </div>
                                </div>
                                <div className="main-card--column flex-column">
                                    {openContractsTooltip && (
                                        <InformativeDate handleClose={handleShowInformativeContractExpiration} />
                                    )}
                                    <label className="column--title">
                                        Contratos próximos a vencer
                                        <TooltipInformation
                                            textTooltip={TOOLTIP_INFORMATION}
                                            iconName="informativeGreen"
                                            typeIcon="svg"
                                            classNameIcon="inside-platform icon--tooltip"
                                            placement="top-end"
                                            classNameTooltip="max-width-inside-platform"
                                        />
                                    </label>
                                    <div className="value__container flex-row">
                                        <label className="other--value">
                                            {data?.month_summary?.next_contracts_to_due?.length || 0}
                                        </label>
                                        {!!data?.month_summary?.next_contracts_to_due.length && (
                                            <ul className="list--dates flex-column">
                                                {data?.month_summary?.next_contracts_to_due?.map(contract => (
                                                    <li
                                                        className="date"
                                                        key={`contract-${contract.employee_id}`}
                                                        onClick={(): void => handleActionEmployeeById(contract.employee_id)}
                                                    >
                                                        {`${contract.employee_name || ''} ${contract.finish_contract_date || ''}`}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="total-payments__container">
                        <SubTitle title="Total Pagos" className="title" />
                        <div className="cards__container flex-row">
                            <div className="card__container flex-column">
                                <label className="card--title">Total pago de nómina/{handleYearPeriodText()}</label>
                                <div className="container__price flex-row">
                                    <Icon name="symbolValueWhite" className="icon--width" type="svg" />
                                    <label className="value">
                                        {formatCurrencyNoSymbol(data?.total_pays?.total_payslip || 0)}
                                    </label>
                                </div>
                            </div>
                            <div className="card__container flex-column">
                                <label className="card--title">Total pago seguridad social</label>
                                <div className="container__price flex-row">
                                    <Icon name="symbolValueWhite" className="icon--width" type="svg" />
                                    <label className="value">
                                        {formatCurrencyNoSymbol(data?.total_pays?.total_social_security || 0)}
                                    </label>
                                </div>
                            </div>
                            <div className="card__container flex-column">
                                <label className="card--title">Total valor a reservar provisiones/{handleYearPeriodText()}</label>
                                <div className="container__price flex-row">
                                    <Icon name="symbolValueWhite" className="icon--width" type="svg" />
                                    <label className="value">
                                        {formatCurrencyNoSymbol(data?.total_pays?.total_social_benefits || 0)}
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="inside-platform__footer flex-row">
                    <ButtonSectionIcon
                        iconName="calendarPriceGreen"
                        iconClassName="icon--width"
                        typeIcon="svg"
                        onClick={handleLiquidation}
                    >
                        Liquidar mi Nómina
                    </ButtonSectionIcon>
                </div>
            </div>
        </>
    );
};
